import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../context/I18nContext";
import {
  Bell, Search, ShieldCheck, Clock, ChevronRight, Mail,
} from "lucide-react";

/** Format inventory value compactly: 14,200,000 → $14.2M */
function fmtCompact(v) {
  const n = Number(v) || 0;
  if (n >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
  if (n >= 1e3) return `$${(n / 1e3).toFixed(0)}k`;
  return `$${n.toFixed(0)}`;
}

function TickerKpi({ label, value, trail, tone, testid, testidLabel }) {
  const color = {
    amber: "text-amber-400",
    blue: "text-blue-400",
    emerald: "text-emerald-400",
    rose: "text-rose-400",
    slate: "text-slate-300",
  }[tone] || "text-slate-300";
  return (
    <div className="flex items-center gap-2 whitespace-nowrap" data-testid={testid}>
      <span className="text-slate-500 uppercase tracking-widest text-[10px]">{label}</span>
      <span className={`font-bold tabular-nums ${color}`} data-testid={testidLabel}>{value}</span>
      {trail && <span className="text-slate-500 text-[10px]">{trail}</span>}
    </div>
  );
}

const CRUMB_MAP = {
  "": { section: "Panel", label: "Panel de Control" },
  "address-book": { section: "Núcleo", label: "Libreta de Direcciones" },
  "items": { section: "Núcleo", label: "Maestro de Artículos" },
  "classifications": { section: "Núcleo", label: "Clasificaciones" },
  "equipment": { section: "Núcleo", label: "Equipos" },
  "cardex": { section: "Núcleo", label: "Cardex" },
  "stock-rotation": { section: "Núcleo", label: "Rotación" },
  "locations": { section: "Almacén", label: "Ubicaciones" },
  "transfers": { section: "Almacén", label: "Transferencias" },
  "vales": { section: "Almacén", label: "Vales de Salida" },
  "cycle-counts": { section: "Almacén", label: "Conteos Cíclicos" },
  "quality": { section: "Almacén", label: "Calidad" },
  "shipments": { section: "Almacén", label: "Embarques" },
  "serials": { section: "Almacén", label: "Series" },
  "scan": { section: "Almacén", label: "Escáner" },
  "requisitions": { section: "Compras", label: "Requisiciones" },
  "purchase-orders": { section: "Compras", label: "Órdenes de Compra" },
  "receipts": { section: "Compras", label: "Recepciones" },
  "match": { section: "Compras", label: "Conciliación 3 Vías" },
  "mrp": { section: "Compras", label: "MRP" },
  "reabasto": { section: "Compras", label: "Reabastecimiento" },
  "internal-requisitions": { section: "Compras", label: "Requisiciones Internas" },
  "mnt": { section: "Mantenimiento", label: "Mantenimiento" },
  "inbox": { section: "Sistema", label: "Bandeja" },
  "personnel": { section: "Sistema", label: "Personal" },
  "departments": { section: "Sistema", label: "Departamentos" },
  "settings": { section: "Sistema", label: "Ajustes" },
};

function buildBreadcrumb(pathname) {
  const clean = pathname.replace(/^\/+|\/+$/g, "");
  if (!clean) return CRUMB_MAP[""];
  const first = clean.split("/")[0];
  return CRUMB_MAP[first] || { section: "MFIS", label: first };
}

function relativeAge(iso) {
  if (!iso) return "—";
  const d = new Date(iso).getTime();
  const diff = Math.max(0, Date.now() - d);
  const m = Math.floor(diff / 60000);
  if (m < 1) return "ahora";
  if (m < 60) return `hace ${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `hace ${h}h`;
  const days = Math.floor(h / 24);
  return `hace ${days}d`;
}

export default function TopRibbon({ ribbon, notifCount = 0, inboxCount = 0 }) {
  const { user } = useAuth();
  const { t } = useI18n();
  const location = useLocation();
  const navigate = useNavigate();
  const [now, setNow] = useState(new Date());

  // Live clock
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  // Keyboard shortcut: ⌘K / Ctrl+K → focus inbox as a global "search" surrogate
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        navigate("/inbox");
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [navigate]);

  if (!user || user.role === "supplier") return null;

  const hms = now.toLocaleTimeString("es-MX", { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false });
  const kpis = ribbon?.kpis || {};
  const crumb = buildBreadcrumb(location.pathname);
  const initials = (user.name || user.email || "??").split(/\s+/).map((s) => s[0]).slice(0, 2).join("").toUpperCase();
  const smtpOk = ribbon?.smtp?.configured;
  const backupAge = relativeAge(ribbon?.backup?.last_at);
  const totalBell = notifCount + inboxCount;

  return (
    <div className="h-9 bg-[#0B1120] text-slate-200 border-b border-slate-800 flex items-stretch text-[11px] font-mono select-none sticky top-0 z-40"
         data-testid="top-ribbon">
      {/* LEFT — server + clock + breadcrumb */}
      <div className="flex items-center gap-4 px-4 border-r border-slate-800">
        <div className="flex items-center gap-1.5" data-testid="ribbon-server-status" title={`Backup: ${backupAge}`}>
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
          </span>
          <span className="text-emerald-400 font-semibold tracking-widest">{ribbon?.region || "PROD-MX"}</span>
          <span className="text-slate-500">·</span>
          <span className="text-slate-400">{(ribbon?.uptime_pct ?? 99.99).toFixed(2)}% UP</span>
        </div>
        <div className="flex items-center gap-1.5 text-slate-300" data-testid="ribbon-live-clock">
          <Clock size={11} className="text-slate-500" />
          <span className="tabular-nums font-semibold">{hms}</span>
          <span className="text-slate-500">CST</span>
        </div>
        <div className="flex items-center gap-1 text-slate-400" data-testid="ribbon-breadcrumb">
          <span className="text-slate-500">MFIS</span>
          <ChevronRight size={10} className="text-slate-600" />
          <span className="text-slate-500">{crumb.section}</span>
          <ChevronRight size={10} className="text-slate-600" />
          <span className="text-white font-semibold">{crumb.label}</span>
        </div>
      </div>

      {/* CENTER — Ticker KPIs */}
      <div className="flex-1 flex items-center justify-center gap-6 overflow-hidden">
        <TickerKpi label="REQ PEND" value={kpis.pending_requisitions ?? "…"}
                   trail={kpis.inventory_value ? "" : ""}
                   tone={(kpis.pending_requisitions || 0) > 0 ? "amber" : "slate"}
                   testid="ribbon-kpi-req" testidLabel="ribbon-kpi-req-value" />
        <span className="text-slate-700">|</span>
        <TickerKpi label="OC ABIERTAS" value={kpis.open_pos ?? "…"}
                   tone="blue"
                   testid="ribbon-kpi-oc" testidLabel="ribbon-kpi-oc-value" />
        <span className="text-slate-700">|</span>
        <TickerKpi label="INV" value={kpis.inventory_value != null ? fmtCompact(kpis.inventory_value) : "…"}
                   trail="MXN"
                   tone="emerald"
                   testid="ribbon-kpi-inv" testidLabel="ribbon-kpi-inv-value" />
        <span className="text-slate-700">|</span>
        <TickerKpi label="STOCK ALERTA" value={kpis.low_stock ?? "…"}
                   trail="SKUs"
                   tone={(kpis.low_stock || 0) > 0 ? "rose" : "slate"}
                   testid="ribbon-kpi-stock" testidLabel="ribbon-kpi-stock-value" />
      </div>

      {/* RIGHT — search, SMTP, AES, notifs, user */}
      <div className="flex items-center gap-3 px-4 border-l border-slate-800">
        <button className="flex items-center gap-1.5 text-slate-400 hover:text-white transition-colors"
                onClick={() => navigate("/inbox")}
                data-testid="ribbon-search-btn">
          <Search size={11} />
          <span>Buscar</span>
          <kbd className="ml-1 px-1 py-0.5 bg-slate-800 border border-slate-700 rounded text-[9px]">⌘K</kbd>
        </button>
        <div className={`flex items-center gap-1 ${smtpOk ? "text-emerald-400" : "text-slate-500"}`}
             data-testid="ribbon-smtp-badge" title={smtpOk ? "Resend/SMTP configurado" : "SMTP no configurado"}>
          <Mail size={10} />
          <span className="text-[10px] font-semibold uppercase tracking-widest">SMTP</span>
        </div>
        <div className="flex items-center gap-1 text-blue-300"
             data-testid="ribbon-trust-badge" title="AES-256 · JWT · Backups automáticos">
          <ShieldCheck size={10} />
          <span className="text-[10px] font-semibold uppercase tracking-widest">
            {ribbon?.security?.encryption || "AES-256"}
          </span>
        </div>
        <button className="relative text-slate-400 hover:text-white"
                onClick={() => navigate("/inbox")}
                data-testid="ribbon-notifications" title="Notificaciones + Bandeja">
          <Bell size={12} />
          {totalBell > 0 && (
            <span className="absolute -top-1 -right-1 h-3 min-w-3 px-0.5 bg-[#F29A2E] text-[8px] text-black font-bold rounded-full flex items-center justify-center"
                  data-testid="ribbon-notifications-badge">
              {totalBell > 99 ? "99+" : totalBell}
            </span>
          )}
        </button>
        <div className="flex items-center gap-2 pl-3 border-l border-slate-800" data-testid="ribbon-user-profile">
          <div className="w-5 h-5 bg-[#F29A2E] text-[#0B1120] font-black text-[10px] flex items-center justify-center rounded-full">{initials}</div>
          <div className="leading-tight text-[10px]">
            <div className="text-white font-semibold truncate max-w-[140px]">{user.name || user.email}</div>
            <div className="text-slate-500 uppercase tracking-widest text-[9px]">
              {t(`role.${user.role}`)}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
